// Renders a real app route inside a desktop window via its own MemoryRouter —
// links/navigation inside the window stay inside the window. The WindowedContext
// flag tells MainLayout to suppress the taskbar / mobile chrome so windows never
// show a second top bar (this replaces the old iframe approach from /dev).
import { memo, type ContextType } from 'react';
import { MemoryRouter, UNSAFE_LocationContext, UNSAFE_RouteContext } from 'react-router-dom';
import { AppRoutes } from '@/AppRoutes';
import { WindowedContext } from '@/lib/context/WindowManagerContext';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';

// react-router v6 forbids nesting a Router inside another Router ("You cannot
// render a <Router> inside another <Router>" — message stripped in production,
// which made pop-out windows crash with a blank Error). Resetting the router
// contexts between the app's BrowserRouter and this window's MemoryRouter makes
// the inner router believe it is top-level. Verified against v6.28.
const NO_LOCATION = null as unknown as ContextType<typeof UNSAFE_LocationContext>;
const ROOT_ROUTE: ContextType<typeof UNSAFE_RouteContext> = {
  outlet: null,
  matches: [],
  isDataRoute: false,
};

export const WindowContent = memo(({ route }: { route: string }) => (
  <WindowedContext.Provider value={true}>
    {/* Boundary: a crash inside a window shows in-window, never blanks the app. */}
    <ErrorBoundary scope="window">
      <UNSAFE_LocationContext.Provider value={NO_LOCATION}>
        <UNSAFE_RouteContext.Provider value={ROOT_ROUTE}>
          <MemoryRouter
            initialEntries={[route]}
            future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
          >
            <AppRoutes />
          </MemoryRouter>
        </UNSAFE_RouteContext.Provider>
      </UNSAFE_LocationContext.Provider>
    </ErrorBoundary>
  </WindowedContext.Provider>
));

WindowContent.displayName = 'WindowContent';
